import React, { useState, useEffect, useRef } from 'react';
import { useProductData } from '../hooks/useProductData';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useFont } from '../../Design/FontContext';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import { getButtonEffectStyles } from '../utils/buttonEffectsUtils';
import { getResponsiveFontSize } from '../utils/responsiveFontUtils';
import './WidgetStyle.css';
import './ResponsiveFonts.css';
import './ProductInfoWidgetGuide.css';
import '../WidgetEditComponents/TextEffectsAnimations.css';
import DeleteModal from './DeleteModal';

function appendPx(value) {
    if (typeof value === 'string' && isNaN(value)) {
        return value;
    }
    return `${value}px`;
}

const ProductInfoWidget = ({ data, isSelected, onRemove, isEditMode }) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const [currentBreakpoint, setCurrentBreakpoint] = useState('normal');
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
    const containerRef = useRef(null);
    const { productData, loading, error } = useProductData(data.productId, refreshCounter);
    
    // Fetch linked product data if button is linked to different product
    const { productData: linkedProductData } = useProductData(data.buttonProductId);
    const deleteLogo = '/img/delete.svg';
    const fonts = useFont();
    

    // Force refresh when component receives focus or when window gets focus
    useEffect(() => {
        const handleFocus = () => {
            console.log('🔄 ProductInfoWidget - Window focused, forcing refresh');
            setRefreshCounter(prev => prev + 1);
        };

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                console.log('🔄 ProductInfoWidget - Page visible, forcing refresh');
                setRefreshCounter(prev => prev + 1);
            }
        };

        const handleProductUpdate = (event) => {
            if (event.detail.productId === data.productId) {
                console.log('🔄 ProductInfoWidget - Product update event received for:', data.productId);
                setRefreshCounter(prev => prev + 1);
            }
        };

        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('productUpdated', handleProductUpdate);

        return () => {
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('productUpdated', handleProductUpdate);
        };
    }, [data.productId]);

    // Also refresh when productId changes
    useEffect(() => {
        console.log('🔄 ProductInfoWidget - ProductId changed, forcing refresh');
        setRefreshCounter(prev => prev + 1);
    }, [data.productId]);
    
    // Debug: Log productData when it changes
    useEffect(() => {
        console.log('🎯 ProductInfoWidget - Effect triggered:', {
            hasProductData: !!productData,
            productId: data.productId,
            loading,
            error,
            timestamp: new Date().toISOString()
        });
        
        if (loading) {
            console.log('⏳ ProductInfoWidget - Loading product data for:', data.productId);
        } else if (error) {
            console.log('❌ ProductInfoWidget - Error loading product:', error);
        } else if (productData) {
            console.log('✅ ProductInfoWidget - Product data received:', {
                id: productData.id,
                title: productData.title,
                price: productData.price,
                currency: productData.currency,
                showDiscount: productData.showDiscount,
                discountType: productData.discountType,
                discountValue: productData.discountValue,
                discountTimeLimited: productData.discountTimeLimited,
                discountExpiryDate: productData.discountExpiryDate,
                discountLabel: productData.discountLabel,
                updatedAt: productData.updatedAt
            });
            
            // Check discount validity
            const isValid = productData.showDiscount && productData.discountValue;
            const isTimeValid = !productData.discountTimeLimited || 
                               !productData.discountExpiryDate || 
                               new Date() < new Date(productData.discountExpiryDate);
            
            console.log('💸 ProductInfoWidget - Discount analysis:', {
                showDiscount: productData.showDiscount,
                hasDiscountValue: !!productData.discountValue,
                discountValue: productData.discountValue,
                isTimeLimited: productData.discountTimeLimited,
                expiryDate: productData.discountExpiryDate,
                isTimeValid,
                finalDiscountValid: isValid && isTimeValid,
                isFreeProduct: productData.price === 0 || productData.price === '0'
            });
        } else {
            console.log('⚪ ProductInfoWidget - No productData available for:', data.productId);
        }
    }, [productData, loading, error, data.productId]);
    
    // Default images
    const defaultHeroImage = '/img/Background_Images.webp';

    // Responsive Layout Detection
    useEffect(() => {
        const checkContainerWidth = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setIsMobileLayout(containerWidth < 750);
                
                if (containerWidth < 750) {
                    setCurrentBreakpoint('mobile');
                } else if (containerWidth > 1400) {
                    setCurrentBreakpoint('big');
                } else {
                    setCurrentBreakpoint('normal');
                }
            }
        };

        checkContainerWidth();
        window.addEventListener('resize', checkContainerWidth);
        
        const observer = new ResizeObserver(checkContainerWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkContainerWidth);
            observer.disconnect();
        };
    }, []);

    const handleDelete = (event, widgetId) => {
        setShowDeleteModal(false);
        if (onRemove) {
            const syntheticEvent = event || { stopPropagation: () => {} };
            onRemove(syntheticEvent, widgetId);
        }
    };

    function isDark(color) {
        const rgb = parseInt(color.slice(1), 16);
        const r = (rgb >> 16) & 0xff;
        const g = (rgb >>  8) & 0xff;
        const b = (rgb >>  0) & 0xff;
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        return luma < 128;
    }

    const getBorderStyle = () => {
        if (isEditMode) {
            if (isSelected) {
                const borderColor = isDark(data.backgroundColor) ? '#FFFFFF' : '#000000';
                return `2px dashed ${borderColor}`;
            } else {
                return data.border ? `1.5px solid ${data.borderColor}` : '2px dashed transparent';
            }
        } else {
            return data.border ? `1.5px solid ${data.borderColor}` : 'none';
        }
    };

    // Image Filter Effects (same as SimplisticQuote)
    const getImageEffect = (effect) => {
        switch (effect) {
            case 'blur': return { filter: 'blur(3px)', gradient: null };
            case 'blur-light': return { filter: 'blur(1px)', gradient: null };
            case 'blur-heavy': return { filter: 'blur(5px)', gradient: null };
            case 'grayscale': return { filter: 'grayscale(100%)', gradient: null };
            case 'top-fade': return { filter: 'none', gradient: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)' };
            case 'bottom-fade': return { filter: 'none', gradient: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)' };
            case 'left-fade': return { filter: 'none', gradient: 'linear-gradient(to right, rgba(0,0,0,0.6), transparent)' };
            case 'right-fade': return { filter: 'none', gradient: 'linear-gradient(to left, rgba(0,0,0,0.6), transparent)' };
            case 'center-glow': return { filter: 'none', gradient: 'radial-gradient(circle at center, transparent 20%, rgba(0,0,0,0.6))' };
            case 'dark-vignette': return { filter: 'none', gradient: 'radial-gradient(circle, transparent 30%, rgba(0,0,0,0.7))' };
            case 'light-vignette': return { filter: 'none', gradient: 'radial-gradient(circle, transparent 30%, rgba(255,255,255,0.7))' };
            default: return { filter: 'none', gradient: null };
        }
    };

    const getSubtypeInfo = (type, subtype) => {
        const subtypeConfig = {
            digital: {
                ebook: { label: 'E-Book', icon: '📖' },
                template: { label: 'Template', icon: '🎨' },
                software: { label: 'Software', icon: '💻' },
                audio: { label: 'Audio Content', icon: '🎵' },
                video: { label: 'Video Content', icon: '🎬' },
                course_materials: { label: 'Course Materials', icon: '📚' },
                preset: { label: 'Presets', icon: '⚙️' },
                other: { label: 'Digital Product', icon: '📦' }
            },
            course: {
                online_course: { label: 'Online Course', icon: '🎓' },
                mini_course: { label: 'Mini Course', icon: '📝' },
                masterclass: { label: 'Masterclass', icon: '👨‍🏫' },
                certification: { label: 'Certification', icon: '🏆' },
                workshop_series: { label: 'Workshop Series', icon: '🔨' }
            }
        };
        
        return subtypeConfig[type]?.[subtype] || { label: 'Product', icon: '📦' };
    };

    const formatPrice = (price, currency) => {
        if (price === 0) return 'Free';
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: currency || 'EUR'
        }).format(price);
    };

    // Discount Functions - Use productData instead of widget data for dynamic updates
    const isDiscountValid = () => {
        if (!productData || !productData.showDiscount || !productData.discountValue) return false;
        
        // Check if discount is time limited
        if (productData.discountTimeLimited && productData.discountExpiryDate) {
            const now = new Date();
            const expiryDate = new Date(productData.discountExpiryDate);
            return now < expiryDate;
        }
        
        // If not time limited, discount is always valid
        return true;
    };

    const calculateDiscountedPrice = (originalPrice) => {
        if (!isDiscountValid() || originalPrice === 0) return originalPrice;
        
        const discountValue = parseFloat(productData.discountValue) || 0;
        
        if (productData.discountType === 'percentage') {
            return originalPrice - (originalPrice * (discountValue / 100));
        } else {
            // Fixed amount discount
            return Math.max(0, originalPrice - discountValue);
        }
    };

    const getDiscountAmount = (originalPrice) => {
        if (!isDiscountValid()) return 0;
        
        const discountedPrice = calculateDiscountedPrice(originalPrice);
        return originalPrice - discountedPrice;
    };

    const getDiscountPercentage = (originalPrice) => {
        if (!isDiscountValid() || originalPrice === 0) return 0;
        
        const discountAmount = getDiscountAmount(originalPrice);
        return Math.round((discountAmount / originalPrice) * 100);
    };

    // Check if product is free
    const isFreeProduct = () => {
        return !productData || productData.price === 0 || productData.price === '0';
    };

    // Check if product has discounts
    const hasDiscount = productData && productData.showDiscount && productData.discountValue;
    const hasTimeLimitedDiscount = hasDiscount && productData.discountTimeLimited && productData.discountExpiryDate;



    // Countdown Timer for Sale Banner
    const [timeLeft, setTimeLeft] = useState('');
    
    useEffect(() => {
        if (!isDiscountValid() || !productData.discountTimeLimited || !productData.discountExpiryDate) {
            setTimeLeft('');
            return;
        }

        const updateCountdown = () => {
            const now = new Date().getTime();
            const expiry = new Date(productData.discountExpiryDate).getTime();
            const difference = expiry - now;

            if (difference > 0) {
                const days = Math.floor(difference / (1000 * 60 * 60 * 24));
                const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((difference % (1000 * 60)) / 1000);

                if (days > 0) {
                    setTimeLeft(`${days}d ${hours}h ${minutes}m`);
                } else if (hours > 0) {
                    setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
                } else {
                    setTimeLeft(`${minutes}m ${seconds}s`);
                }
            } else {
                setTimeLeft('EXPIRED');
            }
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);

        return () => clearInterval(interval);
    }, [productData?.discountExpiryDate, productData?.discountTimeLimited]);


    // Get feature style configuration
    const getFeatureStyle = (index) => {
        const style = data.featureStyle || 'pills';
        
        const baseStyles = {
            display: 'flex',
            alignItems: 'center',
            fontSize: '13px',
            fontWeight: '500',
            color: '#475569',
            fontFamily: data.descriptionFont || fonts.globalSmallFont || 'Arial, sans-serif'
        };

        const checkStyle = {
            width: '16px',
            height: '16px',
            background: data.featuresIconColor || '#10b981',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
        };


        switch (style) {
            case 'pills':
                return {
                    container: {
                        ...baseStyles,
                        gap: '12px',
                        padding: '10px 16px',
                        background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                        borderRadius: '25px',
                        width: isMobileLayout ? 'auto' : 'fit-content',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.8)',
                        transition: 'all 0.2s ease'
                    },
                    check: checkStyle
                };
            
            case 'minimal':
                return {
                    container: {
                        ...baseStyles,
                        gap: '12px',
                        padding: '6px 0',
                        width: isMobileLayout ? 'auto' : 'fit-content',
                        position: 'relative'
                    },
                    check: {
                        ...checkStyle,
                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)',
                        border: '2px solid rgba(16, 185, 129, 0.1)'
                    }
                };
            
            case 'lines':
                return {
                    container: {
                        ...baseStyles,
                        gap: '12px',
                        padding: '12px 0',
                        borderBottom: index < data.customFeatures.slice(0, 5).length - 1 ? '1px solid #e2e8f0' : 'none',
                        width: '100%'
                    },
                    check: checkStyle
                };
            
            case 'bullets':
                return {
                    container: {
                        ...baseStyles,
                        gap: '12px',
                        padding: '6px 0',
                        paddingLeft: '24px',
                        width: '100%',
                        position: 'relative'
                    },
                    check: {
                        position: 'absolute',
                        left: '0',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        ...checkStyle
                    }
                };
            
            case 'cards':
                return {
                    container: {
                        ...baseStyles,
                        gap: '12px',
                        padding: '14px 16px',
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                        marginBottom: '8px',
                        width: isMobileLayout ? 'auto' : 'fit-content'
                    },
                    check: checkStyle
                };
            
            case 'badges':
                return {
                    container: {
                        ...baseStyles,
                        gap: '12px',
                        padding: '8px 16px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                        width: isMobileLayout ? 'auto' : 'fit-content',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        transition: 'all 0.2s ease',
                        position: 'relative',
                        overflow: 'hidden'
                    },
                    check: {
                        ...checkStyle,
                        background: 'rgba(255, 255, 255, 0.25)',
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255, 255, 255, 0.4)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                    }
                };
            
            default:
                return {
                    container: baseStyles,
                    check: checkStyle
                };
        }
    };



    const formatAccessDuration = (days) => {
        if (!days || days === 0) return 'Lifetime Access';
        if (days === 1) return '1 Day Access';
        if (days === 7) return '1 Week Access';
        if (days === 30) return '1 Month Access';
        if (days === 365) return '1 Year Access';
        return `${days} Days Access`;
    };

    // Handle Stripe checkout for paid products
    const handleStripeCheckout = async () => {
        if (!productData || isFreeProduct() || isProcessingCheckout) return;

        // Check if product has Stripe configuration
        if (!productData.stripePriceId) {
            alert('This product is not properly configured for payments. Please contact support.');
            return;
        }

        setIsProcessingCheckout(true);

        try {
            const auth = getAuth();
            const user = auth.currentUser;
            
            const functions = getFunctions();
            const createProductCheckoutSession = httpsCallable(functions, 'createProductCheckoutSession');
            
            const { data: sessionData } = await createProductCheckoutSession({
                userId: user ? user.uid : null, // Optional user ID for logged in users
                productId: data.productId,
                priceId: productData.stripePriceId,
                successUrl: productData.successUrl || `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
                cancelUrl: productData.cancelUrl || window.location.href,
                customerEmail: user ? user.email : null // Pre-fill email if user is logged in
            });

            console.log('Checkout session created:', sessionData);

            // Redirect to Stripe Checkout
            if (sessionData.checkoutUrl) {
                window.location.href = sessionData.checkoutUrl;
            } else {
                // Fallback to Stripe's redirect method
                const stripe = await import('@stripe/stripe-js').then(module => 
                    module.loadStripe('pk_live_51JtPI5EK7wspITU4metLteANz6fXOGY4vIvFtRdJYYg3oYt0W9M0mipHroPArKiCQPaAeU7sZvgH17wU0FMYN69200bLf83Oq7')
                );
                
                const { error } = await stripe.redirectToCheckout({ 
                    sessionId: sessionData.sessionId 
                });

                if (error) {
                    console.error("Stripe checkout error:", error);
                    alert('Payment processing error. Please try again.');
                }
            }
        } catch (error) {
            console.error("Checkout session error:", error);
            alert('Unable to process payment. Please try again or contact support.');
        } finally {
            setIsProcessingCheckout(false);
        }
    };

    const getContentSummary = (productData) => {
        if (productData.type === 'digital') {
            const files = productData.contentData?.uploadedFiles || [];
            const urls = productData.contentData?.downloadUrls || [];
            const totalItems = files.length + urls.length;
            
            if (totalItems === 0) return 'Digital content included';
            if (totalItems === 1) return '1 file included';
            return `${totalItems} files included`;
        }
        
        if (productData.type === 'course') {
            const modules = productData.contentData?.videoModules || [];
            if (modules.length === 0) return 'Course content included';
            if (modules.length === 1) return '1 module included';
            return `${modules.length} modules included`;
        }
        
        return 'Content included';
    };


    // Widget styling
    const widgetStyle = {
        backgroundColor: data.backgroundColor || '#ffffff',
        borderRadius: appendPx(data.borderRadius),
        marginTop: appendPx(data.marginTop),
        marginBottom: appendPx(data.marginBottom),
        minHeight: appendPx(data.height || 500),
        border: getBorderStyle(),
        boxShadow: data.shadow ? `0 4px 20px ${data.shadowColor || 'rgba(0, 0, 0, 0.1)'}` : 'none',
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: 'none',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
    };

    // Text styles
    const titleStyle = {
        color: data.titleColor || '#1e293b',
        fontSize: getResponsiveFontSize(data.titleSize, data.titleSizeMobile, data.titleSizeBig, currentBreakpoint) || '32px',
        fontWeight: getResponsiveFontSize(data.titleWeight, data.titleWeightMobile, data.titleWeightBig, currentBreakpoint) || '700',
        fontFamily: data.titleFont || fonts.globalBigFont || 'Jost, Arial, sans-serif',
        margin: '0 0 16px 0',
        lineHeight: '1.2',
        ...getTextEffectStyles(data.titleEffect)
    };

    const descriptionStyle = {
        color: data.descriptionColor || '#64748b',
        fontSize: getResponsiveFontSize(data.descriptionSize, data.descriptionSizeMobile, data.descriptionSizeBig, currentBreakpoint) || '18px',
        fontWeight: getResponsiveFontSize(data.descriptionWeight, data.descriptionWeightMobile, data.descriptionWeightBig, currentBreakpoint) || '400',
        fontFamily: data.descriptionFont || fonts.globalSmallFont || 'Arial, sans-serif',
        margin: '0 0 20px 0',
        lineHeight: '1.6',
        ...getTextEffectStyles(data.descriptionEffect)
    };

    const priceStyle = {
        color: data.priceColor || '#059669',
        fontSize: getResponsiveFontSize(data.priceSize, data.priceSizeMobile, data.priceSizeBig, currentBreakpoint) || '28px',
        fontWeight: getResponsiveFontSize(data.priceWeight, data.priceWeightMobile, data.priceWeightBig, currentBreakpoint) || '700',
        fontFamily: data.priceFont || fonts.globalBigFont || 'Jost, Arial, sans-serif',
        margin: '0 0 24px 0',
        lineHeight: '1.1'
    };

    const subtypeStyle = {
        color: data.subtypeColor || '#8b5cf6',
        fontSize: `${data.subtypeSize || 14}px`,
        fontWeight: data.subtypeWeight || '600',
        fontFamily: fonts.globalSmallFont || 'Arial, sans-serif',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        margin: '0 0 8px 0'
    };

    const featuresStyle = {
        color: data.featuresColor || '#374151',
        fontSize: `${data.featuresSize || 16}px`,
        fontWeight: data.featuresWeight || '400',
        fontFamily: fonts.globalSmallFont || 'Arial, sans-serif',
        margin: '0 0 20px 0',
        lineHeight: '1.5'
    };

    const buttonStyle = {
        backgroundColor: data.buttonColor || '#667eea',
        color: data.buttonTextColor || '#ffffff',
        border: 'none',
        borderRadius: appendPx(data.buttonBorderRadius),
        padding: `${data.buttonPaddingY || 16}px ${data.buttonPaddingX || 32}px`,
        fontSize: '18px',
        fontWeight: '600',
        fontFamily: data.buttonFont || fonts.globalSmallFont || 'Arial, sans-serif',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        textDecoration: 'none',
        display: 'inline-block',
        alignSelf: isMobileLayout ? 'center' : 'flex-start',
        ...getButtonEffectStyles(data.buttonEffect)
    };

    // Loading State
    if (loading) {
        return (
            <div className="product-info-widget loading" style={widgetStyle} ref={containerRef}>
                {isEditMode && (
                    <div>
                        <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                            <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                        </button>
                        {showDeleteModal && (
                            <DeleteModal
                                isOpen={showDeleteModal}
                                onClose={() => setShowDeleteModal(false)}
                                onConfirm={() => handleDelete(null, data.id)}
                            />
                        )}
                    </div>
                )}
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading product data...</p>
                </div>
            </div>
        );
    }

    // No Product Selected State - Beautiful User Guidance
    if (!data.productId || !productData) {
        return (
            <div className="product-info-widget no-product-selected" style={widgetStyle} ref={containerRef}>
                {isEditMode && (
                    <div>
                        <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                            <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                        </button>
                        {showDeleteModal && (
                            <DeleteModal
                                isOpen={showDeleteModal}
                                onClose={() => setShowDeleteModal(false)}
                                onConfirm={() => handleDelete(null, data.id)}
                            />
                        )}
                    </div>
                )}
                <div className="product-selection-guide">
                    <div className="guide-icon">
                        <div className="icon-circle">
                            <i className="fas fa-box-open"></i>
                        </div>
                        <div className="pulse-ring"></div>
                    </div>
                    <div className="guide-content">
                        <h3 className="guide-title">Select Your Product</h3>
                        <p className="guide-description">
                            Choose a digital product or course from your collection to showcase it beautifully with this widget.
                        </p>
                        <div className="guide-steps">
                            <div className="step">
                                <span className="step-number">1</span>
                                <span className="step-text">Open the Product tab in the left panel</span>
                            </div>
                            <div className="step">
                                <span className="step-number">2</span>
                                <span className="step-text">Select your product from the dropdown</span>
                            </div>
                            <div className="step">
                                <span className="step-number">3</span>
                                <span className="step-text">Add this widget to showcase your product</span>
                            </div>
                        </div>
                        <div className="guide-note">
                            <i className="fas fa-info-circle"></i>
                            <span>This widget works with Digital Products and Online Courses</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error State for Wrong Product Types
    if (error || (productData && productData.type !== 'digital' && productData.type !== 'course')) {
        const errorMessage = productData?.type === 'event' ?
            'This widget is only for Digital Products and Courses. Events are not supported.' :
            'Invalid product type. Only Digital Products and Courses are supported.';
            
        return (
            <div className="product-info-widget error" style={widgetStyle} ref={containerRef}>
                {isEditMode && (
                    <div>
                        <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                            <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                        </button>
                        {showDeleteModal && (
                            <DeleteModal
                                isOpen={showDeleteModal}
                                onClose={() => setShowDeleteModal(false)}
                                onConfirm={() => handleDelete(null, data.id)}
                            />
                        )}
                    </div>
                )}
                <div className="error-message">
                    <span className="error-icon">
                        {productData?.type === 'event' ? '🎟️' : '⚠️'}
                    </span>
                    <div>
                        <h4>
                            {productData?.type === 'event' ? 'Event Product Detected' : 'Invalid Product Type'}
                        </h4>
                        <p>{error || errorMessage}</p>
                        {productData?.type === 'event' && (
                            <p style={{ fontSize: '12px', marginTop: '8px', opacity: 0.8 }}>
                                Use the Event Widget for event products instead.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        );
    }


    // Render Content Function
    const renderContent = () => {
        return (
        <>

            {/* Product Type & Subtype Badge */}
            {data.showSubtype && productData.subtype && (
                <div style={subtypeStyle}>
                    {getSubtypeInfo(productData.type, productData.subtype).icon} {getSubtypeInfo(productData.type, productData.subtype).label}
                </div>
            )}

            {/* Title */}
            {data.showTitle && productData.title && (
                <h2 style={titleStyle}>{productData.title}</h2>
            )}

            {/* Description */}
            {data.showDescription && productData.description && (
                <p style={descriptionStyle}>{productData.description}</p>
            )}

            {/* Custom Features (user-definiert) */}
            {data.showCustomFeatures && data.customFeatures && data.customFeatures.length > 0 && (
                <div style={{ 
                    display: 'flex', 
                    flexDirection: (data.featureStyle === 'lines' || data.featureStyle === 'bullets') ? 'column' : (isMobileLayout ? 'row' : 'column'),
                    flexWrap: (data.featureStyle === 'lines' || data.featureStyle === 'bullets') ? 'nowrap' : (isMobileLayout ? 'wrap' : 'nowrap'),
                    gap: data.featureStyle === 'lines' ? '0' : '12px', 
                    marginBottom: '20px',
                    justifyContent: isMobileLayout ? 'center' : 'flex-start',
                    alignItems: isMobileLayout ? 'center' : 'flex-start'
                }}>
                    {data.customFeatures.slice(0, 5).map((feature, index) => {
                        const featureStyles = getFeatureStyle(index);
                        return (
                            <div key={index} style={featureStyles.container}>
                                <div style={featureStyles.check}>
                                <img 
                                    src="/img/done.svg" 
                                    alt="done" 
                                    style={{ 
                                            width: '10px', 
                                            height: '10px',
                                        filter: 'invert(1)' // Always white
                                    }} 
                                />
                            </div>
                            <span>{feature.text}</span>
                        </div>
                        );
                    })}
                </div>
            )}




            {/* Call-to-Action Button */}
            {data.showButton && (
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: isMobileLayout ? 'center' : 'flex-start',
                    gap: '12px'
                }}>
                    {/* Price Display - Above Button */}
                    {data.showPrice && !isFreeProduct() && (
                        <div style={{ marginBottom: '-2px', marginTop: '15px' }}>
                            {isDiscountValid() ? (
                                <div>
                                    
                                    {/* Price with Discount - Smaller */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        gap: '8px',
                                        flexWrap: 'wrap',
                                        justifyContent: isMobileLayout ? 'center' : 'flex-start'
                                    }}>
                                        {/* Discounted Price - Smaller */}
                                        <span style={{
                                            fontFamily: data.secondaryFont || 'inherit',
                                            fontSize: getResponsiveFontSize(data.priceSize * 0.8, data.priceSizeMobile * 0.8, data.priceSizeBig * 0.8, currentBreakpoint) || '20px',
                                            fontWeight: '700',
                                            color: data.priceColor || '#1f2937'
                                        }}>
                                            {formatPrice(calculateDiscountedPrice(productData.price), productData.currency)}
                                        </span>
                                        
                                        {/* Original Price (Strikethrough) - Even smaller */}
                                        <span style={{
                                            fontFamily: data.secondaryFont || 'inherit',
                                            fontSize: getResponsiveFontSize(data.priceSize * 0.6, data.priceSizeMobile * 0.6, data.priceSizeBig * 0.6, currentBreakpoint) || '16px',
                                            color: '#9ca3af',
                                            textDecoration: 'line-through',
                                            fontWeight: '500'
                                        }}>
                                            {formatPrice(productData.price, productData.currency)}
                                        </span>

                                    </div>
                                </div>
                            ) : (
                                // Regular Price (No Discount) - Smaller
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    gap: '8px',
                                    justifyContent: isMobileLayout ? 'center' : 'flex-start'
                                }}>
                                    <span style={{
                                        fontFamily: data.secondaryFont || 'inherit',
                                        fontSize: getResponsiveFontSize(data.priceSize * 0.8, data.priceSizeMobile * 0.8, data.priceSizeBig * 0.8, currentBreakpoint) || '20px',
                                        fontWeight: '700',
                                        color: data.priceColor || '#1f2937'
                                    }}>
                                        {formatPrice(productData.price, productData.currency)}
                                    </span>
                                    
                                </div>
                            )}
                        </div>
                    )}

                <button 
                    style={buttonStyle}
                    onClick={() => {
                            if (isEditMode) return;
                            
                            // Debug info before any action
                            console.log('=== BUTTON CLICK DEBUG ===');
                            console.log('productData:', productData);
                            console.log('data.buttonLink:', data.buttonLink);
                            console.log('data.useStripeCheckout:', data.useStripeCheckout);
                            console.log('isFreeProduct():', isFreeProduct());
                            console.log('productData?.stripePriceId:', productData?.stripePriceId);
                            console.log('========================');
                            
                        // Check if button is linked to a different product
                        if (data.buttonProductId) {
                            console.log('→ Button linked to different product:', data.buttonProductId);
                            
                            if (linkedProductData) {
                                const isLinkedProductFree = linkedProductData.price === 0 || linkedProductData.price === '0';
                                
                            if (isLinkedProductFree) {
                                // Free product - show form popup
                                console.log('→ Linked product is free - showing form popup');
                                // Pass the product data along with the trigger
                                const event = new CustomEvent('triggerProductFormPopup', {
                                    detail: { 
                                        productId: data.buttonProductId,
                                        trigger: 'productInfoWidget',
                                        productData: linkedProductData  // Pass product data directly
                                    }
                                });
                                window.dispatchEvent(event);
                                } else {
                                    // Paid product - redirect to product page (let the product page handle Stripe)
                                    console.log('→ Linked product is paid - redirecting to product page');
                                    window.location.href = `/product/${data.buttonProductId}`;
                                }
                            } else {
                                // Product data not loaded yet, fallback to product page
                                console.log('→ Linked product data not loaded - redirecting to product page');
                                window.location.href = `/product/${data.buttonProductId}`;
                            }
                        } else if (data.buttonLink) {
                                console.log('→ Opening custom button link');
                            window.open(data.buttonLink, '_blank');
                            } else if (productData && isFreeProduct() && productData.type === 'digital') {
                                // Free digital product - show form popup instead of Stripe
                                console.log('→ Opening form popup for free digital product');
                                // Pass the product data along with the trigger
                                const event = new CustomEvent('triggerProductFormPopup', {
                                    detail: { 
                                        productId: data.productId,
                                        trigger: 'productInfoWidget',
                                        productData: productData  // Pass product data directly
                                    }
                                });
                                window.dispatchEvent(event);
                            } else if (productData && !isFreeProduct() && data.useStripeCheckout && productData.enableStripePayment !== false) {
                                if (productData.stripePriceId) {
                                    // Paid product with Stripe checkout enabled - use Stripe
                                    console.log('→ Triggering Stripe checkout');
                                    handleStripeCheckout();
                                } else {
                                    // Demo mode for testing - use fake Stripe data
                                    console.log('→ DEMO MODE: Using test Stripe configuration');
                                    alert('DEMO MODE: Would normally redirect to Stripe Checkout now!\n\nIn real mode, you need to configure:\n- Stripe Product ID (e.g. prod_ABC123)\n- Stripe Price ID (e.g. price_XYZ789)\n\nFor now, simulating Stripe redirect...');
                                    
                                    // Simulate what would happen with real Stripe
                                    const demoProductData = {
                                        ...productData,
                                        stripePriceId: 'price_DEMO123' // Demo price ID
                                    };
                                    
                                    console.log('Demo: Would call handleStripeCheckout with:', demoProductData);
                                    
                                    // Show what the Stripe call would look like
                                    setTimeout(() => {
                                        alert(`DEMO: Stripe Checkout would be called with:
                                        
Product ID: ${data.productId}
Price ID: price_DEMO123
Success URL: ${window.location.origin}/success
Cancel URL: ${window.location.href}

In real mode, user would be redirected to Stripe payment page.`);
                                    }, 1000);
                                    
                                    return;
                                }
                            } else if (productData && !isFreeProduct() && productData.enableFormOnly !== false) {
                                // Paid product with form-only enabled - show form popup
                                console.log('→ Product configured for form-only - showing form popup');
                                const event = new CustomEvent('triggerProductFormPopup', {
                                    detail: { 
                                        productId: data.productId,
                                        trigger: 'productInfoWidget',
                                        productData: productData,
                                        paymentMethod: 'form-only'
                                    }
                                });
                                window.dispatchEvent(event);
                            } else if (productData && !isFreeProduct() && data.buttonLink) {
                                // Paid product without Stripe checkout - use custom link
                                console.log('→ Opening product custom link');
                                window.open(data.buttonLink, '_blank');
                            } else {
                                // Show alert before redirect so user can see the debug info
                                const debugInfo = `
Debug Info:
- productData exists: ${!!productData}
- is free product: ${isFreeProduct()}
- useStripeCheckout: ${data.useStripeCheckout}
- has stripePriceId: ${!!productData?.stripePriceId}
- has buttonLink: ${!!data.buttonLink}

Will redirect to: /product/${data.productId}
                                `;
                                alert(debugInfo);
                                console.log('→ Fallback: Redirecting to product page');
                                window.location.href = `/product/${data.productId}`;
                            }
                        }}
                        disabled={isProcessingCheckout && data.useStripeCheckout}
                    onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'none';
                    }}
                >
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            justifyContent: 'center'
                        }}>
                            <span>
                                {(isProcessingCheckout && data.useStripeCheckout)
                                    ? 'Processing...' 
                                    : (data.showButtonText !== false ? (data.buttonText || (isFreeProduct() ? 'Get Free Access' : 'Get Instant Access')) : (isFreeProduct() ? 'Get Free Access' : 'Get Instant Access'))
                                }
                            </span>
                            
                            {/* FREE Badge inside button */}
                            {isFreeProduct() && (
                                <span style={{
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    color: 'white',
                                    padding: '2px 6px',
                                    borderRadius: '8px',
                                    fontSize: '10px',
                                    fontWeight: '700',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    marginLeft: '4px'
                                }}>
                                    FREE
                                </span>
                            )}
                            
                            {/* Secondary Discount Badge in Button */}
                            {data.showDiscountBadge !== false && isDiscountValid() && !isFreeProduct() && (() => {
                                const discountText = productData.discountType === 'percentage' 
                                    ? `-${getDiscountPercentage(productData.price)}%`
                                    : (productData.discountLabel || 'SALE');
                                    
                                return (
                                    <span style={{
                                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                        color: 'white',
                                        padding: '3px 8px',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.3px',
                                        boxShadow: '0 2px 6px rgba(239, 68, 68, 0.3)'
                                    }}>
                                        {discountText}
                                    </span>
                                );
                            })()}
                        </div>
                </button>
                    
                    {/* Secure Payment with Stripe - Only show for paid products */}
                    {data.showStripePayment !== false && !isFreeProduct() && productData?.enableStripePayment !== false && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '12px',
                            color: data.stripeTextColor || '#64748b',
                            fontFamily: fonts.globalSmallFont || 'Arial, sans-serif'
                        }}>
                            <img 
                                src="/img/stripe.svg" 
                                alt="Stripe" 
                                style={{ 
                                    width: '32px', 
                                    height: '32px',
                                    opacity: 0.8
                                }} 
                            />
                            <span>Secure payment with Stripe</span>
                        </div>
                    )}
                </div>
            )}
        </>
        );
    };

    return (
        <div className="product-info-widget" style={{
            ...widgetStyle,
            borderRadius: appendPx(data.borderRadius)
        }} ref={containerRef}>
            
            {/* Background Image Container (like SimplisticQuote) */}
            {data.backgroundImage && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1
                }}>
                    <img 
                        src={data.backgroundImage || defaultHeroImage}
                        alt="Background"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            opacity: data.backgroundImageOpacity > 1 ? 
                                (parseFloat(data.backgroundImageOpacity) / 100) : // Prozent-Wert (10-100) zu Dezimal
                                (parseFloat(data.backgroundImageOpacity) || 0.8), // Bereits Dezimal-Wert (0.1-1.0)
                            filter: data.imageEffect ? getImageEffect(data.imageEffect).filter : 'none'
                        }}
                    />
                    
                    {/* Gradient/Effect Overlay */}
                    {data.imageEffect && getImageEffect(data.imageEffect).gradient && (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: getImageEffect(data.imageEffect).gradient
                        }} />
                    )}
                </div>
            )}

            {/* Banner Mode Image - Desktop: Side Banner */}
            {data.showProductImage && data.productImage && data.productImageMode === 'banner' && !isMobileLayout && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    [data.bannerImageAlignment === 'left' ? 'left' : 'right']: 0,
                    width: `${data.bannerImageWidth || 40}%`,
                    zIndex: 1
                }}>
                    <img 
                        src={data.productImage}
                        alt={productData.title}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: data.productImageShape === 'rectangle' ? '0' : 
                                         data.productImageShape === 'circle' ? '0' : // No circle in banner mode
                                         `${data.productImageBorderRadius || 0}px`
                        }}
                    />
                </div>
            )}
            
            {/* Delete Button */}
            {isEditMode && (
                <div>
                    <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                        <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                    </button>
                    
                    
                    {showDeleteModal && (
                        <DeleteModal
                            isOpen={showDeleteModal}
                            onClose={() => setShowDeleteModal(false)}
                            onConfirm={() => handleDelete(null, data.id)}
                        />
                    )}
                </div>
            )}

            {/* Mobile Banner Mode: Image at top, content below */}
            {isMobileLayout && data.productImageMode === 'banner' ? (
                <div style={{
                    position: 'relative',
                    zIndex: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {/* Top Banner Image */}
                    {data.showProductImage && data.productImage && (
                        <div style={{
                            width: '100%',
                            height: '200px',
                            marginBottom: '24px',
                            flexShrink: 0
                        }}>
                            <img 
                                src={data.productImage}
                                alt={productData.title}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: data.productImageShape === 'rectangle' ? '0' : 
                                                 data.productImageShape === 'circle' ? '20px' : 
                                                 `${data.productImageBorderRadius || 12}px`
                                }}
                            />
                        </div>
                    )}
                    
                    {/* Content below image */}
                    <div style={{
                        padding: `0 ${data.padding || 40}px ${data.padding || 40}px ${data.padding || 40}px`,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        textAlign: 'center'
                    }}>
                        {renderContent()}
                    </div>
                </div>
            ) : (
                /* Desktop Layout or Normal Mode */
                <div style={{
                    position: 'relative',
                    zIndex: 2,
                    padding: data.productImageMode === 'banner' && !isMobileLayout ? 
                            // Desktop Banner Mode Padding
                            `${data.padding || 40}px ${data.bannerImageAlignment === 'right' ? `calc(${data.bannerImageWidth || 40}% + 24px)` : `${data.padding || 40}px`} ${data.padding || 40}px ${data.bannerImageAlignment === 'left' ? `calc(${data.bannerImageWidth || 40}% + 24px)` : `${data.padding || 40}px`}` :
                            // Normal Mode Padding
                            appendPx(data.padding || 40),
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    textAlign: isMobileLayout ? 'center' : 'left'
                }}>
                    
                    {data.productImageMode === 'banner' ? (
                        // Banner Mode: Only text content
                        renderContent()
                    ) : (
                        // Normal Mode: Layout with image positioning
                        <div style={{
                            display: 'flex',
                            flexDirection: isMobileLayout || data.productImagePosition === 'top' || data.productImagePosition === 'bottom' ? 'column' : 'row',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '24px',
                            height: '100%',
                            width: '100%'
                        }}>
                            
                            {/* Product Image - Top/Left Position (Normal Mode) */}
                            {data.showProductImage && data.productImage &&
                             (data.productImagePosition === 'top' || data.productImagePosition === 'left' || 
                              (isMobileLayout && data.productImagePosition !== 'bottom')) && (
                                <div style={{
                                    flexShrink: 0,
                                    width: isMobileLayout ? '100%' : `${data.productImageWidth || 300}px`,
                                    maxWidth: isMobileLayout ? '400px' : 'none',
                                    order: isMobileLayout ? -1 : 0,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}>
                                    {data.productImageShape === 'circle' ? (
                                        <div style={{
                                            width: `${Math.min(data.productImageWidth || 300, data.productImageHeight || 300)}px`,
                                            height: `${Math.min(data.productImageWidth || 300, data.productImageHeight || 300)}px`,
                                            borderRadius: '50%',
                                            overflow: 'hidden',
                                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                                        }}>
                                            <img 
                                                src={data.productImage}
                                                alt={productData.title}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <img 
                                            src={data.productImage}
                                            alt={productData.title}
                                            style={{
                                                width: '100%',
                                                height: data.productImageHeight ? `${data.productImageHeight}px` : 'auto',
                                                objectFit: 'cover',
                                                borderRadius: data.productImageShape === 'rectangle' ? '0' : 
                                                             `${data.productImageBorderRadius || 12}px`,
                                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                                            }}
                                        />
                                    )}
                                </div>
                            )}

                            {/* Text Content Container */}
                            <div style={{ 
                                flex: 1, 
                                minWidth: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                            }}>
                                {renderContent()}
                            </div>

                            {/* Product Image - Right Position (Normal Mode) */}
                            {data.showProductImage && data.productImage &&
                             data.productImagePosition === 'right' && !isMobileLayout && (
                                <div style={{
                                    flexShrink: 0,
                                    width: `${data.productImageWidth || 300}px`,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}>
                                    {data.productImageShape === 'circle' ? (
                                        <div style={{
                                            width: `${Math.min(data.productImageWidth || 300, data.productImageHeight || 300)}px`,
                                            height: `${Math.min(data.productImageWidth || 300, data.productImageHeight || 300)}px`,
                                            borderRadius: '50%',
                                            overflow: 'hidden',
                                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                                        }}>
                                            <img 
                                                src={data.productImage}
                                                alt={productData.title}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <img 
                                            src={data.productImage}
                                            alt={productData.title}
                                            style={{
                                                width: '100%',
                                                height: data.productImageHeight ? `${data.productImageHeight}px` : 'auto',
                                                objectFit: 'cover',
                                                borderRadius: data.productImageShape === 'rectangle' ? '0' : 
                                                             `${data.productImageBorderRadius || 12}px`,
                                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                                            }}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Product Image - Bottom Position (Normal Mode) */}
                    {data.productImageMode === 'normal' && data.showProductImage && data.productImage && 
                     data.productImagePosition === 'bottom' && (
                        <div style={{
                            width: '100%',
                            marginTop: '24px',
                            display: 'flex',
                            justifyContent: 'center'
                        }}>
                            {data.productImageShape === 'circle' ? (
                                <div style={{
                                    width: `${Math.min(data.productImageWidth || 300, data.productImageHeight || 300)}px`,
                                    height: `${Math.min(data.productImageWidth || 300, data.productImageHeight || 300)}px`,
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                                }}>
                                    <img 
                                        src={data.productImage}
                                        alt={productData.title}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </div>
                            ) : (
                                <img 
                                    src={data.productImage}
                                    alt={productData.title}
                                    style={{
                                        width: '100%',
                                        maxWidth: `${data.productImageWidth || 400}px`,
                                        height: data.productImageHeight ? `${data.productImageHeight}px` : 'auto',
                                        objectFit: 'cover',
                                        borderRadius: data.productImageShape === 'rectangle' ? '0' : 
                                                     `${data.productImageBorderRadius || 12}px`,
                                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                                    }}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}
            

        </div>
    );
};

export default ProductInfoWidget;